import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import ModulePaymentPlans from '../components/ModulePaymentPlans';
import ModuleCoupons from '../components/ModuleCoupons';
import useAuthStore from '../store/authStore';
import api, { academicService, enrollmentService } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import { CURRENCY_OPTIONS } from '../utils/currency';

function EditModule() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAuthenticated } = useAuthStore();
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [grades, setGrades] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [myPaymentAccounts, setMyPaymentAccounts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    grade: '',
    teachers: [],
    image: null,
    intro_video: null,
    intro_video_url: '',
    price: '0.00',
    currency: 'USD',
    enrollment_directives: '',
    sequential_progress: false,
    requires_approval: true,
    payment_account_ids: [],
  });
  const [currentThumbnail, setCurrentThumbnail] = useState(null);
  const [currentIntroVideo, setCurrentIntroVideo] = useState(null);
  const [errors, setErrors] = useState({});

  const isAdmin = user?.roles?.some(role => role.name?.toLowerCase() === 'administrator');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const isTeacher = user?.roles?.some(role => role.name?.toLowerCase() === 'teacher');
    if (!isTeacher && !isAdmin) {
      addNotification(t('Only teachers or administrators can edit modules'), 'error');
      navigate('/courses');
      return;
    }

    fetchData();
    fetchMyPaymentAccounts();
    if (isAdmin) fetchTeachers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, navigate, user, id]);

  const fetchMyPaymentAccounts = async () => {
    try {
      const res = await enrollmentService.getPaymentAccounts();
      const data = res.data;
      const list = Array.isArray(data) ? data : data.results || [];
      setMyPaymentAccounts(list.filter((a) => !a.is_platform_default));
    } catch (error) {
      // non-fatal — the picker just stays empty
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await api.get('/users/', { params: { role: 'teacher' } });
      const data = response.data || response;
      setTeachers(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      addNotification(t('Failed to load teachers.'), 'error');
    }
  };

  const toggleTeacher = (teacherId) => {
    setFormData(prev => ({
      ...prev,
      teachers: prev.teachers.includes(teacherId)
        ? prev.teachers.filter(tid => tid !== teacherId)
        : [...prev.teachers, teacherId]
    }));
  };

  const fetchData = async () => {
    try {
      setFetchingData(true);

      // Fetch module data
      const moduleResponse = await academicService.getModule(id);
      const moduleData = moduleResponse.data || moduleResponse;

      // Fetch grades
      const gradesResponse = await academicService.getGrades();
      const gradesData = gradesResponse.data || gradesResponse;
      setGrades(Array.isArray(gradesData) ? gradesData : gradesData.results || []);

      // Set form data
      setFormData({
        name: moduleData.name || '',
        code: moduleData.code || '',
        description: moduleData.description || '',
        grade: moduleData.grades && moduleData.grades.length > 0 ? moduleData.grades[0].id : '',
        teachers: (moduleData.teachers || []).map(t => t.id),
        price: moduleData.price ? String(moduleData.price) : '0.00',
        currency: moduleData.currency || 'USD',
        enrollment_directives: moduleData.enrollment_directives || '',
        sequential_progress: !!moduleData.sequential_progress,
        requires_approval: moduleData.requires_approval !== false,
        payment_account_ids: moduleData.payment_account_ids || [],
        intro_video_url: moduleData.intro_video || moduleData.intro_video_url || ''
      });
      // Set current thumbnail preview if available
      if (moduleData.thumbnail) {
        setCurrentThumbnail(moduleData.thumbnail);
      }
      if (moduleData.intro_video) {
        setCurrentIntroVideo(moduleData.intro_video);
      }
    } catch (error) {
      addNotification(t('Failed to load module data'), 'error');
      navigate('/courses');
    } finally {
      setFetchingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, image: file }));
    if (file) {
      try {
        const preview = URL.createObjectURL(file);
        setCurrentThumbnail(preview);
      } catch (err) {
        // ignore
      }
    }
  };

  const handleIntroVideoChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, intro_video: file }));
    if (file) {
      try {
        const preview = URL.createObjectURL(file);
        setCurrentIntroVideo(preview);
      } catch (err) {
        // ignore
      }
    }
  };

  const handleIntroVideoUrlChange = (e) => {
    setFormData(prev => ({ ...prev, intro_video_url: e.target.value }));
    setCurrentIntroVideo(e.target.value);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t('Module name is required');
    if (!formData.code.trim()) newErrors.code = t('Module code is required');
    if (!formData.grade) newErrors.grade = t('Grade is required');
    if (isAdmin && formData.teachers.length === 0) newErrors.teachers = t('Select at least one teacher');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        grades: [parseInt(formData.grade)] // Backend expects grades as an array
        ,price: parseFloat(formData.price || 0),
        currency: formData.currency,
        enrollment_directives: formData.enrollment_directives,
        sequential_progress: formData.sequential_progress,
        requires_approval: formData.requires_approval,
        payment_accounts: formData.payment_account_ids,
      };
      if (isAdmin) {
        submitData.teachers = formData.teachers;
      }

      // Files and regular fields belong to one update. Besides avoiding a
      // partially-updated module when a file write fails, this lets Django
      // parse and validate the entire edit as a single multipart request.
      if (formData.image || formData.intro_video) {
        const fd = new FormData();
        Object.entries(submitData).forEach(([key, value]) => {
          if (value === null || value === undefined) return;
          if (Array.isArray(value)) fd.append(key, JSON.stringify(value));
          else fd.append(key, value);
        });
        if (formData.image) fd.append('thumbnail', formData.image);
        if (formData.intro_video) fd.append('intro_video', formData.intro_video);
        if (formData.intro_video_url && !formData.intro_video) {
          fd.append('intro_video_url', formData.intro_video_url);
        }
        await academicService.updateModule(id, fd);
      } else {
        await academicService.updateModule(id, {
          ...submitData,
          intro_video_url: formData.intro_video_url || '',
        });
      }

      addNotification(t('Module updated successfully!'), 'success');
      navigate(`/modules/${id}`);
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          setErrors(errorData);
          // Show first error message
          const firstError = Object.values(errorData)[0];
          addNotification(`${t('Failed')}: ${typeof firstError === 'string' ? firstError : JSON.stringify(firstError)}`, 'error');
        } else {
          // Production Django deliberately returns an HTML page for an
          // unexpected server error. Showing that markup in a toast is noisy
          // and leaks an implementation detail without helping the user.
          const isHtml = typeof errorData === 'string' && /<!doctype|<html/i.test(errorData);
          addNotification(
            isHtml ? t('Failed to update module. Please try again.') : `${t('Failed to update module')}: ${errorData}`,
            'error'
          );
        }
      } else if (error.message) {
        addNotification(`${t('Failed to update module')}: ${error.message}`, 'error');
      } else {
        addNotification(t('Failed to update module. Please try again.'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchingData) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <TopNav title={t('Edit Module')} subtitle={t('Loading...')} />
          <main className="flex-1 overflow-y-auto p-6">
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Edit Module')} subtitle={t('Update module information')} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-md p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Module Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Module Name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('e.g., Introduction to Mathematics')}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                {/* Module Code */}
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Module Code')} *
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.code ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={t('e.g., MATH101')}
                  />
                  {errors.code && <p className="mt-1 text-sm text-red-600">{errors.code}</p>}
                </div>

                {/* Grade */}
                <div>
                  <label htmlFor="grade" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Grade Level')} *
                  </label>
                  <select
                    id="grade"
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      errors.grade ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">{t('Select a grade')}</option>
                    {grades.map(grade => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name}
                      </option>
                    ))}
                  </select>
                  {errors.grade && <p className="mt-1 text-sm text-red-600">{errors.grade}</p>}
                </div>

                {/* Teacher assignment (admin only) */}
                {isAdmin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Assign Teachers')}
                    </label>
                    <div className="border border-gray-300 rounded-lg divide-y max-h-48 overflow-y-auto">
                      {teachers.length === 0 ? (
                        <p className="text-sm text-gray-500 p-3">{t('No approved teachers available')}</p>
                      ) : (
                        teachers.map(teacher => (
                          <label key={teacher.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.teachers.includes(teacher.id)}
                              onChange={() => toggleTeacher(teacher.id)}
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{teacher.first_name} {teacher.last_name} <span className="text-gray-400">({teacher.email})</span></span>
                          </label>
                        ))
                      )}
                    </div>
                    {errors.teachers && <p className="mt-1 text-sm text-red-600">{errors.teachers}</p>}
                  </div>
                )}

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Description')}
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={t('Provide a brief description of the module...')}
                  />
                </div>

                {/* Price */}
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">{t('Price')}</label>
                    <input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">{t('Currency')}</label>
                    <select
                      id="currency"
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                    >
                      {CURRENCY_OPTIONS.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Enrollment Instructions */}
                <div>
                  <label htmlFor="enrollment_directives" className="block text-sm font-medium text-gray-700 mb-2">{t('Enrollment Instructions (optional)')}</label>
                  <textarea
                    id="enrollment_directives"
                    name="enrollment_directives"
                    value={formData.enrollment_directives}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder={t('Any special instructions for enrollment (requirements, documents, etc.)')}
                  />
                </div>

                {/* Sequential progress */}
                <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <input
                    id="sequential_progress"
                    name="sequential_progress"
                    type="checkbox"
                    checked={formData.sequential_progress}
                    onChange={(e) => setFormData(prev => ({ ...prev, sequential_progress: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="sequential_progress" className="text-sm text-gray-700">
                    <span className="font-medium block">{t('Lock lessons in order')}</span>
                    {t('Students must complete each lesson before the next one unlocks.')}
                  </label>
                </div>

                {/* Requires approval */}
                <div className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                  <input
                    id="requires_approval"
                    name="requires_approval"
                    type="checkbox"
                    checked={formData.requires_approval}
                    onChange={(e) => setFormData(prev => ({ ...prev, requires_approval: e.target.checked }))}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="requires_approval" className="text-sm text-gray-700">
                    <span className="font-medium block">{t('Require approval before students can access this course')}</span>
                    {t('Turn this off for free/open courses — students get access immediately, with no teacher or admin approval step.')}
                  </label>
                </div>

                {/* Payment accounts */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">{t('Payment Accounts Shown to Students')}</label>
                    <Link to="/payment-accounts" className="text-xs text-primary-600 hover:underline">{t('+ Manage my payment accounts')}</Link>
                  </div>
                  <div className="border border-gray-300 rounded-lg divide-y max-h-48 overflow-y-auto">
                    {myPaymentAccounts.length === 0 ? (
                      <p className="text-sm text-gray-500 p-3">{t("You haven't added any payment accounts yet — the platform's default account will be shown to students instead.")}</p>
                    ) : (
                      myPaymentAccounts.map((acc) => (
                        <label key={acc.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.payment_account_ids.includes(acc.id)}
                            onChange={() => setFormData(prev => ({
                              ...prev,
                              payment_account_ids: prev.payment_account_ids.includes(acc.id)
                                ? prev.payment_account_ids.filter((x) => x !== acc.id)
                                : [...prev.payment_account_ids, acc.id]
                            }))}
                            className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{acc.label} <span className="text-gray-400">({acc.method_type})</span></span>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {t("If none are selected, students see the platform's default payment account instead.")}
                  </p>
                </div>

                {/* Image (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Module Image (optional)')}</label>
                  <div className="flex items-center gap-4">
                    <label htmlFor="image" className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                      <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7M3 7l9 6 9-6" />
                      </svg>
                      {t('Upload image')}
                    </label>
                    <input
                      id="image"
                      name="thumbnail"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="text-sm text-gray-600">
                      {formData.image ? (
                        <span className="font-medium">{formData.image.name}</span>
                      ) : (
                        <span className="text-gray-500">{t('No file chosen')}</span>
                      )}
                    </div>
                  </div>
                    {currentThumbnail && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-700 mb-2">{t('Current image preview:')}</p>
                        <img src={currentThumbnail} alt={t('Module thumbnail')} className="w-48 h-32 object-cover rounded-md border" />
                      </div>
                    )}
                  <p className="mt-1 text-xs text-gray-500">{t('Supported formats: JPG, PNG. Max 5MB.')}</p>
                </div>

                {/* Intro video (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Intro Video (optional)')}</label>
                  <div className="flex items-center gap-4">
                    <label htmlFor="intro_video" className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                      {t('Upload intro video')}
                    </label>
                    <input
                      id="intro_video"
                      name="intro_video"
                      type="file"
                      accept="video/*"
                      onChange={handleIntroVideoChange}
                      className="hidden"
                    />
                    <div className="text-sm text-gray-600">
                      {formData.intro_video ? (
                        <span className="font-medium">{formData.intro_video.name}</span>
                      ) : (
                        <span className="text-gray-500">{t('No file chosen')}</span>
                      )}
                    </div>
                  </div>
                  {currentIntroVideo && (
                    <div className="mt-4">
                      <p className="text-sm text-gray-700 mb-2">{t('Current intro video preview:')}</p>
                      <video src={currentIntroVideo} controls className="w-full rounded-md border" />
                    </div>
                  )}
                  <p className="mt-1 text-xs text-gray-500">{t('Supported: mp4, webm. Max 50MB.')}</p>
                </div>

                <div>
                  <label htmlFor="intro_video_url" className="block text-sm font-medium text-gray-700 mb-2">{t('Or Intro Video URL (YouTube etc.)')}</label>
                  <input
                    id="intro_video_url"
                    name="intro_video_url"
                    type="text"
                    value={formData.intro_video_url}
                    onChange={handleIntroVideoUrlChange}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('Provide a video link instead of uploading a file.')}</p>
                </div>

                {/* Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/modules/${id}`)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? t('Updating...') : t('Update Module')}
                  </button>
                </div>
              </form>
            </div>

            <div className="mt-6 space-y-6">
              <ModulePaymentPlans moduleId={id} />
              <ModuleCoupons moduleId={id} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default EditModule;
